import {
  FC,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
} from 'react';
import { Check, ChevronDown, Search, Loader2 } from 'lucide-react';
import * as Select from '@radix-ui/react-select';
import { debounce } from 'lodash';
import { cn } from '@new/lib';
import { useGetChatUsersQuery } from '@teachur/http-client';

const USERS_PER_PAGE = 30;
const DEBOUNCE_DELAY_MS = 300;
const INTERSECTION_THRESHOLD = 0.1;
const SIDE_OFFSET = 4;

const ICON_SIZE_SM = 'w-4 h-4';
const ICON_SIZE_MD = 'w-5 h-5';

const LOADING_SPINNER_CLASSES = 'animate-spin text-neutral-400';
const CENTERED_FLEX = 'flex items-center justify-center';
const TEXT_MUTED = 'text-neutral-500';
const TEXT_EXTRA_SMALL = 'text-xs m-0!';

interface UserSelectorProps {
  value: number | null;
  onSelect: (userId: number | null) => void;
}

const getUserFullName = (firstName: string, lastName: string) => `${firstName} ${lastName}`;

const getPluralText = (count: number, singular: string, plural: string) =>
  count === 1 ? singular : plural;

export const UserSelector: FC<UserSelectorProps> = ({ value, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const scrollRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const { users, isLoading, hasMore, fetchNextPage, isFetchingNextPage } = useGetChatUsersQuery({
    searchTerm: debouncedSearchTerm,
    limit: USERS_PER_PAGE,
  });

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTerm(value);
      }, DEBOUNCE_DELAY_MS),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const onSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const onLoadMore = useCallback(() => {
    if (hasMore && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasMore, isFetchingNextPage, fetchNextPage]);

  const onValueChange = (newValue: string) => {
    onSelect(newValue ? Number(newValue) : null);
  };

  const onSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onSearchChange(e.target.value);
  };

  const onSearchInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const onSearchInputClick = (e: MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (!isOpen || !hasMore || isFetchingNextPage) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: INTERSECTION_THRESHOLD }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isOpen, hasMore, isFetchingNextPage, onLoadMore]);

  return (
    <Select.Root
      value={value?.toString() || ''}
      onValueChange={onValueChange}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Select.Trigger
        className={cn(
          'w-full flex items-center justify-between',
          'bg-neutral-100 rounded-lg px-4 py-2.5 text-sm',
          'outline-none focus:ring-2 focus:ring-sky-300',
          'transition-colors hover:bg-neutral-200',
          'data-placeholder:text-neutral-400'
        )}
      >
        <Select.Value placeholder="Select a user..." />
        <Select.Icon>
          <ChevronDown className="w-4 h-4 text-neutral-500" />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className="bg-(--neutral-white-NW1000) rounded-lg shadow-lg border border-neutral-200 overflow-hidden z-50 w-[var(--radix-select-trigger-width)]"
          position="popper"
          sideOffset={SIDE_OFFSET}
        >
          <div className="p-2 border-b border-neutral-200 sticky top-0 bg-(--neutral-white-NW1000) z-10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={onSearchInputChange}
                onKeyDown={onSearchInputKeyDown}
                onClick={onSearchInputClick}
                placeholder="Search users..."
                className="w-full bg-neutral-50 rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300"
              />
            </div>
          </div>

          <Select.Viewport ref={scrollRef} className="p-1 max-h-64 overflow-y-auto">
            {isLoading && users.length === 0 ? (
              <div className={cn(CENTERED_FLEX, 'py-8')}>
                <Loader2 className={cn(ICON_SIZE_MD, LOADING_SPINNER_CLASSES)} />
                <span className={cn('ml-2 text-sm', TEXT_MUTED)}>Loading users...</span>
              </div>
            ) : users.length === 0 ? (
              <div className={cn('px-4 py-8 text-center text-sm', TEXT_MUTED)}>
                {searchTerm ? 'No users found' : 'Start typing to search users'}
              </div>
            ) : (
              <>
                {users.map((user) => (
                  <Select.Item
                    key={user.id}
                    value={user.id.toString()}
                    className={cn(
                      'flex items-center justify-between px-3 py-2.5 text-sm cursor-pointer rounded-md',
                      'outline-none transition-colors',
                      'data-highlighted:bg-neutral-100',
                      'data-[state=checked]:bg-sky-50'
                    )}
                  >
                    <div className="flex flex-col flex-1">
                      <Select.ItemText>
                        <span className="font-medium text-neutral-900">
                          {getUserFullName(user.firstName, user.lastName)}
                        </span>
                      </Select.ItemText>
                    </div>
                    <Select.ItemIndicator>
                      <Check className={cn(ICON_SIZE_SM, 'text-sky-600')} />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
                {hasMore && (
                  <div
                    ref={loadMoreRef}
                    className="px-4 py-3 text-center border-t border-neutral-100 mt-1"
                  >
                    {isFetchingNextPage ? (
                      <div className={CENTERED_FLEX}>
                        <Loader2 className={cn(ICON_SIZE_SM, LOADING_SPINNER_CLASSES)} />
                        <span className={cn('ml-2', TEXT_EXTRA_SMALL, TEXT_MUTED)}>
                          Loading more...
                        </span>
                      </div>
                    ) : (
                      <span className={cn(TEXT_EXTRA_SMALL, 'text-neutral-400')}>
                        Scroll for more
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </Select.Viewport>
          {users.length > 0 && (
            <div className="px-4 py-2 border-t border-neutral-200 bg-neutral-50">
              <p className={cn(TEXT_EXTRA_SMALL, TEXT_MUTED)}>
                Showing {users.length} {getPluralText(users.length, 'user', 'users')}
                {hasMore && ' • Scroll for more'}
              </p>
            </div>
          )}
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};
